/*
    Notifications (#9465): the header bell and the View all page (/admin/notifications)

    Both are a [data-notifications] root holding an All/Unread tab strip, a list and a
    "Mark all as read" button (the page adds "Delete all"; each row has a delete button, #9506),
    and share NotificationList below. The bell's badge count is
    fetched once per page load (and again when the tab becomes visible after a while); a list
    is only fetched when it is shown. Server: UserNotificationController.

    Unread changes are broadcast as a "ds:notifications-unread" event on document so that
    reading on the View all page also updates the bell badge.
*/
const UNREAD_EVENT = "ds:notifications-unread";

// Category (stored lowercase, UserNotificationCategory) -> icon, tint and default action label
const CATEGORIES = {
    approval: { icon: "fa-check", tone: "blue", linkText: "Review" },
    failure: { icon: "fa-triangle-exclamation", tone: "red", linkText: "See details" },
    export: { icon: "fa-arrow-down-to-line", tone: "green", linkText: "Download" },
    import: { icon: "fa-arrow-up-from-line", tone: "gray", linkText: "View" },
    sms: { icon: "fa-comment", tone: "purple", linkText: "Reply" },
    segment: { icon: "fa-users", tone: "blue", linkText: "View segment" },
    coupon: { icon: "fa-ticket", tone: "orange", linkText: "View codes" },
    productupdate: { icon: "fa-bullhorn", tone: "orange", linkText: "Learn more" },
    integration: { icon: "fa-plug", tone: "red", linkText: "Review integration" },
    general: { icon: "fa-bell", tone: "gray", linkText: "View" }
};

function escapeHtml(value) {
    const div = document.createElement("div");
    div.textContent = value ?? "";
    return div.innerHTML;
}

// Only http(s) and app-relative links are rendered as links; anything else (javascript:, data:)
// is dropped rather than trusted, since broadcast urls are typed in by hand.
function safeUrl(url) {
    if (!url) return null;
    if (url.startsWith("/") && !url.startsWith("//")) return url;
    if (/^https?:\/\//i.test(url)) return url;
    return null;
}

function startOfToday() {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
}

function timeAgo(isoUtc) {
    const date = new Date(isoUtc);
    const seconds = Math.max(0, (Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min ago`;
    if (date.getTime() >= startOfToday()) return `${Math.floor(minutes / 60)} hr ago`;
    if (date.getTime() >= startOfToday() - 24 * 60 * 60 * 1000) return "Yesterday";
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// The badge is a glanceable "there is something", not a counter
function badgeText(unread) {
    return unread > 9 ? "9+" : String(unread);
}

function renderItem(n) {
    const category = CATEGORIES[n.category] || CATEGORIES.general;
    const url = safeUrl(n.url);
    const external = url && /^https?:\/\//i.test(url) && !url.startsWith(location.origin);

    const meta = [];
    if (n.scope !== "user") meta.push('<span class="notifications-item__scope">Announcement</span>');
    meta.push(`<span>${timeAgo(n.date_created_utc)}</span>`);
    if (url) {
        meta.push(`<a class="notifications-item__action" href="${escapeHtml(url)}"${external ? ' target="_blank" rel="noopener"' : ""}>${escapeHtml(n.link_text || category.linkText)}</a>`);
    }

    return `<li class="notifications-item${n.is_read ? "" : " unread"}${url ? " has-link" : ""}" data-id="${n.id}">
                <span class="notifications-item__icon notifications-tone-${category.tone}" aria-hidden="true"><i class="fa-regular ${category.icon}"></i></span>
                <div class="notifications-item__text">
                    <div class="notifications-item__title">${escapeHtml(n.title)}</div>
                    ${n.body ? `<div class="notifications-item__body">${escapeHtml(n.body)}</div>` : ""}
                    <div class="notifications-item__meta">${meta.join('<span class="notifications-item__sep" aria-hidden="true">&middot;</span>')}</div>
                </div>
                <button type="button" class="notifications-item__dot" title="Mark as read" aria-label="Mark as read"></button>
                <button type="button" class="notifications-item__delete" title="Delete" aria-label="Delete notification"><i class="fa-regular fa-trash-can" aria-hidden="true"></i></button>
            </li>`;
}

function fetchNotifications(query) {
    return fetch(`/api/v1/notifications?${new URLSearchParams(query)}`, {
        headers: { "Accept": "application/json" }
    })
        .then(response => response.ok ? response.json() : null)
        .then(json => (json && json.Status) ? json.Response : null)
        .catch(() => null);
}

function announceUnread(unread) {
    document.dispatchEvent(new CustomEvent(UNREAD_EVENT, { detail: { unread } }));
}

/*
    One list of notifications with All/Unread tabs, grouped Today / Earlier, paged by
    pageSize. A [data-notifications-more] button, when present, loads the next page.
*/
function NotificationList(root, pageSize) {
    const list = root.querySelector("[data-notifications-list]");
    const markAll = root.querySelector("[data-notifications-mark-all]");
    const deleteAll = root.querySelector("[data-notifications-delete-all]");
    const more = root.querySelector("[data-notifications-more]");
    const unreadCount = root.querySelector("[data-notifications-unread-count]");
    const tabs = root.querySelectorAll(".notifications-tab");

    // Whichever tab the markup marks active: Unread in the bell, All on the View all page.
    // Every load() goes back to it, so the bell always reopens on Unread.
    const defaultFilter = root.querySelector(".notifications-tab.active")?.dataset.filter || "all";
    let filter = defaultFilter;
    let loaded = 0;
    let lastGroup = null;
    let unread = 0;
    let request = 0;

    function setUnread(value, announce) {
        unread = Math.max(0, value);
        if (unreadCount) unreadCount.textContent = unread > 0 ? ` · ${unread}` : "";
        if (markAll) markAll.disabled = unread === 0;
        if (announce) announceUnread(unread);
    }

    function empty(message) {
        list.innerHTML = `<li class="notifications-panel__empty">${message}</li>`;
    }

    function showEmpty() {
        empty(filter === "unread" ? "You're all caught up." : "No notifications in the last 30 days.");
    }

    function setLoaded(value) {
        loaded = Math.max(0, value);
        if (deleteAll) deleteAll.disabled = loaded === 0;
    }

    function append(notifications) {
        const today = startOfToday();

        list.insertAdjacentHTML("beforeend", notifications.map(n => {
            const group = new Date(n.date_created_utc).getTime() >= today ? "Today" : "Earlier";
            const heading = group !== lastGroup ? `<li class="notifications-group" role="presentation">${group}</li>` : "";
            lastGroup = group;
            return heading + renderItem(n);
        }).join(""));
    }

    function load(reset) {
        const current = ++request;

        if (reset) {
            setLoaded(0);
            lastGroup = null;
            empty("Loading&hellip;");
        }
        if (more) more.disabled = true;

        fetchNotifications({ unread: filter === "unread", skip: loaded, take: pageSize }).then(data => {
            if (current !== request) return; // a newer tab switch won

            if (!data) {
                if (reset) empty("Notifications could not be loaded.");
                return;
            }

            setUnread(data.unread, true);

            if (reset) list.innerHTML = "";

            const notifications = data.notifications || [];
            append(notifications);
            setLoaded(loaded + notifications.length);

            if (loaded === 0) showEmpty();

            if (more) {
                more.hidden = notifications.length < pageSize;
                more.disabled = false;
            }
        });
    }

    function markRead(item) {
        if (!item || !item.classList.contains("unread")) return;

        item.classList.remove("unread");
        setUnread(unread - 1, true);

        // keepalive so the request survives the navigation the click may be about to start
        fetch(`/api/v1/notifications/${item.dataset.id}/read`, { method: "POST", keepalive: true });
    }

    // Deleting is per-user (announcements are shared rows), so the server just hides it for them
    function remove(item) {
        const heading = item.previousElementSibling;
        const next = item.nextElementSibling;

        if (item.classList.contains("unread")) setUnread(unread - 1, true);
        item.remove();

        // Drop a Today/Earlier heading left with nothing under it
        if (heading?.classList.contains("notifications-group") && (!next || next.classList.contains("notifications-group"))) {
            heading.remove();
            if (lastGroup === heading.textContent) lastGroup = null;
        }

        // The server no longer counts it, so the next page starts one earlier
        setLoaded(loaded - 1);

        const deleted = fetch(`/api/v1/notifications/${item.dataset.id}`, { method: "DELETE" });

        // Emptied the list: there may be more on the server, so refetch once the delete has landed
        if (loaded === 0) {
            empty("Loading&hellip;");
            deleted.catch(() => null).then(() => load(true));
        }
    }

    list.addEventListener("click", function (e) {
        const item = e.target.closest(".notifications-item");
        if (!item) return;

        if (e.target.closest(".notifications-item__delete")) {
            e.preventDefault();
            remove(item);
            return;
        }

        // The dot only marks read; the action link (stretched over the row) marks read and navigates
        if (e.target.closest(".notifications-item__dot")) e.preventDefault();
        markRead(item);
    });

    function selectTab(value) {
        filter = value;
        tabs.forEach(t => {
            const active = t.dataset.filter === value;
            t.classList.toggle("active", active);
            t.setAttribute("aria-selected", active ? "true" : "false");
        });
    }

    tabs.forEach(tab => tab.addEventListener("click", function () {
        if (tab.dataset.filter === filter) return;

        selectTab(tab.dataset.filter);
        load(true);
    }));

    markAll?.addEventListener("click", function () {
        markAll.disabled = true;
        list.querySelectorAll(".notifications-item.unread").forEach(i => i.classList.remove("unread"));
        setUnread(0, true);

        fetch("/api/v1/notifications/read", { method: "POST" });
    });

    // Deletes everything, not just the open tab
    deleteAll?.addEventListener("click", function () {
        if (!window.confirm("Delete all of your notifications? This can't be undone.")) return;

        request++; // drop any page still loading
        setLoaded(0);
        setUnread(0, true);
        lastGroup = null;
        if (more) more.hidden = true;
        showEmpty();

        fetch("/api/v1/notifications", { method: "DELETE" });
    });

    more?.addEventListener("click", () => load(false));

    return {
        load: () => {
            selectTab(defaultFilter);
            load(true);
        },
        setUnread
    };
}

globalThis.DsNotifications = { NotificationList };

document.addEventListener("DOMContentLoaded", function () {
    const bell = document.getElementById("navbar_notifications");
    const badge = document.getElementById("notifications_badge");
    const panel = document.getElementById("notifications_panel");

    if (bell && badge && panel) {
        const toggle = bell.querySelector(".notifications-bell");
        const bellList = NotificationList(panel, 20);
        const REFRESH_AFTER_MS = 60 * 1000;
        let lastCountAt = 0;

        const setBadge = function (unread) {
            badge.textContent = badgeText(unread);
            badge.classList.toggle("d-none", !(unread > 0));
            toggle?.setAttribute("aria-label", unread > 0 ? `Notifications (${unread} unread)` : "Notifications");
        };

        const refreshCount = function () {
            lastCountAt = Date.now();
            fetchNotifications({ countOnly: true }).then(data => {
                if (!data) return;
                setBadge(data.unread);
                bellList.setUnread(data.unread, false);
            });
        };

        document.addEventListener(UNREAD_EVENT, e => setBadge(e.detail.unread));

        // Bootstrap 3 dropdown: reload the list each time it opens
        $(bell).on("show.bs.dropdown", bellList.load);

        // Clicks inside the panel must not bubble to Bootstrap's document handler, or it closes
        // (links still navigate: this stops propagation, not the default action)
        panel.addEventListener("click", e => e.stopPropagation());

        document.addEventListener("visibilitychange", function () {
            if (document.visibilityState === "visible" && Date.now() - lastCountAt > REFRESH_AFTER_MS)
                refreshCount();
        });

        refreshCount();
    }

    // View all page
    const page = document.getElementById("notifications_page");
    if (page) NotificationList(page, 25).load();
});
